import React, { ReactNode, useEffect, useRef } from 'react'

import styled from 'styled-components'

const Backdrop = styled.div<{ $open: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  align-items: flex-end;
  justify-content: center;
`

const SheetContainer = styled.div<{ $snapPercent: string }>`
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: 16px 16px 0 0;
  width: 100%;
  max-width: 600px;
  max-height: ${({ $snapPercent }) => $snapPercent};
  min-height: 200px;
  overflow-y: auto;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`

const HandleBar = styled.div`
  width: 40px;
  height: 4px;
  background-color: ${({ theme }) => theme.colors.info};
  border-radius: 2px;
  margin: 8px auto;
`

interface IProps {
  open?: boolean
  initialSnap: 1 | 2 | 3 | 4
  onClose: () => void
  children: ReactNode
  handle?: () => JSX.Element
}

export function SheetModal({
  open,
  onClose,
  children,
  handle,
  initialSnap = 2,
}: IProps) {
  const snapPercents = ['25%', '50%', '75%', '90%']
  const backdropRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) {
      onClose()
    }
  }

  return (
    <Backdrop $open={!!open} ref={backdropRef} onClick={handleBackdropClick}>
      <SheetContainer $snapPercent={snapPercents[initialSnap - 1]}>
        {handle ? handle() : <HandleBar />}
        {children}
      </SheetContainer>
    </Backdrop>
  )
}
